import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata, RegexFormattingOptions } from "@tylertech/tyler-api-types";
import { Subject, Subscription } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataInputFormatterService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";

@Component({
    selector: "metadata-input-text",
    templateUrl: "./metadata-input-text.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputTextComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    modelGroup = inject(NgModelGroup);
    private $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataInputFormatterService = inject(MetadataInputFormatterService);
    private readonly $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    @Input()
    model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    viewOnly: boolean;

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    dense: boolean;

    @Input()
    forCellEdit: boolean;

    @Input()
    hint: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    error: string;

    @Input()
    compact: boolean;

    @Input()
    showSpinner: boolean;

    @ViewChild("input") input: ElementRef;

    /** Accessible in the template */ isViewOnly: boolean;

    cellEditInPopup: boolean = false;

    private isInitialized: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string = "";

    private currentLength: number = 0;

    private readonly debouncer$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    private _density: string;
    private subscriptionForKeyStroke: Subscription;
    private keyStroke$ = new Subject<number>();
    private lastKeyTime = 0;
    private isScanning = false;

    ngOnInit(): void
    {
        this.model = this.applyFormatting(this.model);
        this.unboundModel = this.model;
        this.currentLength = this.model ? this.model.length : 0;

        if (this.forCellEdit)
            this.debouncer$.pipe(debounceTime(10), takeUntil(this.destroy$)).subscribe(() => this.fireValueChanged());

        this._density = this.$metadataService.getInputDensity(this.compact);

        if (this.forCellEdit)
            this.cellEditInPopup = this.$metadataInputTypeHelperService.isTextArea(this.propertyMetadata.InputType) || this.propertyMetadata.EditInPopup;

        if (this.propertyMetadata.RecalculateOnScan === true)
        {
            const scannerInputTimeout = 300;
            this.subscriptionForKeyStroke = this.keyStroke$.pipe(
                debounceTime(scannerInputTimeout) // If no new event comes in after 200ms, assume the scanner is done
            ).subscribe(() =>
            {
                if (this.isScanning)
                {
                    this.scanComplete();
                }
            });
        }
    }

    ngOnDestroy(): void
    {
        this.input = null;
        if (this.forCellEdit)
        {
            this.destroy$.next();
            this.destroy$.complete();
            this.debouncer$.complete();
        }

        if (this.propertyMetadata.RecalculateOnScan === true)
        {
            this.subscriptionForKeyStroke.unsubscribe();
        }
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
    }

    @HostListener("focusin", ["$event"])
    onFocus(event: any): void
    {
        if (!this.forCellEdit)
            this.input.nativeElement.select();
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    this.onModelChanged(change.currentValue);
                    break;
            }
        }
    }

    get isInvalid(): boolean
    {
        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        if (!value)
        {
            this.valueModel = null;
            this.currentLength = 0;
        }
        else if (!this.valueModel || this.valueModel !== value)
        {
            value = this.applyFormatting(value);
            this.valueModel = value;
            this.currentLength = this.valueModel ? this.valueModel.length : 0;
        }
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        if (Utilities.IsNullOrUndefined(value) && this.model === "" && this.isRequired) this._valueModel = "";
        else this._valueModel = value;
    }

    get InputType(): string
    {
        if (Utilities.IsNullOrUndefined(this.propertyMetadata)) return "";
        return this.propertyMetadata.InputType;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get Regex(): string
    {
        return this.$metadataPropertyService.getRegex(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get RegexOptions(): RegexFormattingOptions
    {
        return this.$metadataPropertyService.getRegexFormatter(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get MinLength(): number
    {
        return this.propertyMetadata.MinLength;
    }

    get MaxLength(): number
    {
        return this.$metadataInputTypeHelperService.getMaxLength(this.propertyMetadata, this.metadataModel, this.parent);
    }

    GetToolTipText(): string
    {
        return this.propertyMetadata.TooltipText;
    }

    HasTooltip(): boolean
    {
        return !!this.propertyMetadata && !!this.propertyMetadata.Tooltip;
    }

    Rows(): number
    {
        if (this.propertyMetadata.Rows) return this.propertyMetadata.Rows;
        return 5;
    }

    get isRequired(): boolean
    {
        return (
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) &&
            !this.isViewOnly
        );
    }

    get showCounter(): boolean
    {
        return this.MaxLength && !this.isViewOnly && ((!this.dense && !this.compact ) || this.cellEditInPopup);
    }

    get showHint(): boolean
    {
        return this.hint && !this.isInvalid && ((!this.dense && !this.compact) || this.cellEditInPopup);
    }

    get showError(): boolean
    {
        return this.isInvalid && ((!this.dense && !this.compact) || this.cellEditInPopup);
    }

    onInputChange(): void
    {
        if (this.forCellEdit)
            this.debouncer$.next();
    }

    onChange(): void
    {
        if (!this.forCellEdit)
            this.fireValueChanged();
    }

    scanComplete(): void
    {
        this.fireValueChanged();
        this.isScanning = false;
    }

    fireValueChanged(): void
    {
        const scanned = this.isScanning;
        if (this.RegexOptions && this.RegexOptions.BackEndValueRegexReplace)
        {
            const currentModel = this.$metadataInputFormatterService.GetFormattedModel(this.valueModel, this.RegexOptions);
            if (currentModel === this.model)
            {
                return;
            }
            this.onValueChanged.emit({ value: currentModel, triggeredByScan: scanned });
            return;
        }
        if (this.model === this.valueModel)
        {
            return;
        }

        this.valueModel = this.applyFormatting(this.valueModel);
        this.onValueChanged.emit({ value: this.valueModel, triggeredByScan: scanned });

    }

    applyFormatting(model: any): any
    {
        if (this.RegexOptions)
            model = this.$metadataInputFormatterService.GetFormattedValueModel(
                model,
                this.RegexOptions
            );
        return model;
    }

    onKeydown(event: KeyboardEvent, textArea = false): void
    {
        this.detectChangeByScanner();
        this.enforceFormat(event, textArea);
    }

    enforceFormat(event: KeyboardEvent, textArea = false): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
        else if (!textArea && this.RegexOptions && this.RegexOptions.ValidCharRegex && !this.$keyHandlerService.ignoreForFormatting(event))
        {
            const keyHit = event.key;
            if (keyHit.match(new RegExp(this.RegexOptions.ValidCharRegex)) == null)
            {
                event.preventDefault();
            }
        }
    }

    detectChangeByScanner(): void
    {
        if (this.propertyMetadata.RecalculateOnScan === true)
        {
            const now = Date.now();
            const diff = now - this.lastKeyTime;
            // in milliseconds. This should work for fast and slower scanners,
            // but may misfire if typing in very fast which shouldn't happen under normal usage
            const maxInterCharacterDelay = 150;

            if (diff < maxInterCharacterDelay)
            {
                this.isScanning = true;
            }
            else
            {
                this.isScanning = false;
            }
            this.lastKeyTime = now;
            this.keyStroke$.next(now);
        }
    }

    updateCurrentLength(event): void
    {
        this.currentLength = event.target.value.length;
        if (this.RegexOptions)
        {
            if (!this.$keyHandlerService.ignoreForFormatting(event))
            {
                event.target.value = this.$metadataInputFormatterService.GetFormattedValueModel(event.target.value, this.RegexOptions);
                this.valueModel = event.target.value;
            }
        }
    }
}
